import { ArtichokeService } from "@fidget/common/services/artichokeService";
import { StorageService } from "@fidget/common/services/storageService";
import {
  isSharedPopupState,
  SharedPopupState,
  SharedPopupStoreClass
} from "@fidget/common/store/sharedPopupStore";
import { logger } from "@fidget/common/utils";
import { AuthService } from "./services/authService";
import { HostWindowRequests } from "./services/hostWindowRequests";
import { InitialContextService } from "./services/initialContextService";
import { foreRingtoneService, RingtoneService } from "./services/ringtoneService";
import { ForeSpinnerService, IForeSpinnerService } from "./services/spinnerService";
import { ForeState, ForeStore } from "./store/main";
import { Language } from "@fidget/common/models/language";
import { staticConfig } from "@fidget/common/config/static-config";
import {
  BackOfficeFieldPatch,
  GuestProfileFormPatch
} from "@closerplatform/spinner-openapi";
import { ForeServices } from "./services/foreServices";
import { FidgetService } from "./services/fidgetService";

export interface ForeContext extends WidgetContext {
  state: ForeState;

  readonly orgId: string;
  readonly language: Language;
  authService: AuthService;
  spinnerService: IForeSpinnerService;
  fidgetService: FidgetService;
  hostWindowRequests: HostWindowRequests;
  storageService: StorageService<SharedPopupState>;
  initialContextService: InitialContextService;
  ringtoneService: RingtoneService;
  continueConversationToken: string | undefined;
  joinMeetingToken: string | undefined;
  cancelMeetingToken: string | undefined;
  autoClientCalling: boolean;
  alwaysAutoCall: boolean;
  callingOnAssigneeChanged: boolean;
  botAgentName: string | undefined;
  callToNewAssigneeMessage: string | undefined;
  forceNewUser?: boolean;

  toJSON: () => any;
  resetState: (reinit: boolean) => void;
  handleTagGroupChange: (id: string | undefined) => Promise<void>;
  handleTagsChange: (tags: string[]) => void;
}

export interface GuestProfileContext {
  guestProfile: GuestProfileFormPatch;
  spinnerService: IForeSpinnerService;
  patchGuestProfile: (newData: GuestProfileFormPatch) => Promise<{ tagGroupId?: string }>;
}

export class GuestProfileContextClass implements GuestProfileContext {
  spinnerService: IForeSpinnerService = ForeSpinnerService.getInstance();
  guestProfile: GuestProfileFormPatch = {};

  patchGuestProfile(newData: GuestProfileFormPatch): Promise<{ tagGroupId?: string }> {
    logger.info(`patchGuestProfile(${JSON.stringify(newData)})`);
    const guestProfile = {
      ...this.guestProfile,
      ...newData,
      backOfficeData: this.mergeBackOfficeData(
        this.guestProfile.backOfficeData,
        newData.backOfficeData
      )
    };
    if (JSON.stringify(guestProfile) !== JSON.stringify(this.guestProfile)) {
      this.guestProfile = guestProfile;
      return this.spinnerService.identifyUser(this.guestProfile);
    } else {
      return Promise.resolve({});
    }
  }

  private backOfficeDataToRecord = (backOfficeData?: Array<BackOfficeFieldPatch>): Record<string, BackOfficeFieldPatch> =>
    (backOfficeData || []).reduce((a, b) => ({...a, [b.key]: b}), {});

  private recordToBackOfficeData = (record: Record<string, BackOfficeFieldPatch>): Array<BackOfficeFieldPatch> =>
    Object.keys(record).map((k) => record[k]!).sort((a, b) => a.key.localeCompare(b.key));

  private mergeBackOfficeData = (oldBackOfficeData?: Array<BackOfficeFieldPatch>, newBackOfficeData?: Array<BackOfficeFieldPatch>) => {
    const merged = this.recordToBackOfficeData({
      ...this.backOfficeDataToRecord(oldBackOfficeData),
      ...this.backOfficeDataToRecord(newBackOfficeData),
    });
    return merged.length ? merged : undefined;
  };
}

export interface WidgetContext {
  state: {
    shared: SharedPopupState;
  };
  artichokeService: ArtichokeService;
  storageService: StorageService<SharedPopupState>;
  spinnerService: IForeSpinnerService;
}

type TokenKey = "continueConversationToken" | "joinMeetingToken" | "cancelMeetingToken";
type Tokens = {
  [key in TokenKey]: string;
};

export interface ForeContextInitialData {
  orgId: string;
  language: Language;
  hostWindowRequests: HostWindowRequests;
  tokens: Tokens;
  autoClientCalling: boolean;
  alwaysAutoCall: boolean;
  callingOnAssigneeChanged: boolean;
  botAgentName?: string;
  callToNewAssigneeMessage?: string;
  forceNewUser?: boolean;
}

export class ForeContextClass implements ForeContext {
  readonly orgId: string;
  readonly language: Language;
  fidgetService: FidgetService;
  spinnerService: IForeSpinnerService;
  artichokeService: ArtichokeService;
  initialContextService: InitialContextService = new InitialContextService();
  ringtoneService: RingtoneService = foreRingtoneService;

  store: ForeStore;
  authService: AuthService;
  hostWindowRequests: HostWindowRequests;
  storageService: StorageService<SharedPopupState>;

  continueConversationToken: string | undefined;
  joinMeetingToken: string | undefined;
  cancelMeetingToken: string | undefined;

  autoClientCalling: boolean;
  alwaysAutoCall: boolean;
  callingOnAssigneeChanged: boolean;
  botAgentName: string | undefined;
  callToNewAssigneeMessage: string | undefined;
  forceNewUser: boolean | undefined;

  constructor(
    initialData: ForeContextInitialData,
    { artichokeService, spinnerService }: ForeServices
  ) {
    this.spinnerService = spinnerService;
    this.artichokeService = artichokeService;
    this.orgId = initialData.orgId;
    this.language = initialData.language;
    this.spinnerService.setOrgId(initialData.orgId);
    this.authService = new AuthService(initialData.orgId, initialData.hostWindowRequests);
    this.fidgetService = new FidgetService(initialData.orgId);
    const sharedPopupStore = new SharedPopupStoreClass();
    this.storageService = new StorageService(
      initialData.orgId,
      sharedPopupStore,
      isSharedPopupState
    );

    this.store = new ForeStore(sharedPopupStore.state, this.spinnerService);
    this.hostWindowRequests = initialData.hostWindowRequests;
    this.autoClientCalling = initialData.autoClientCalling;
    this.alwaysAutoCall = initialData.alwaysAutoCall;
    this.callingOnAssigneeChanged = initialData.callingOnAssigneeChanged;
    this.botAgentName = initialData.botAgentName;
    this.callToNewAssigneeMessage = initialData.callToNewAssigneeMessage;
    this.forceNewUser = initialData.forceNewUser;

    Object.assign(this, initialData.tokens);
  }
  async handleTagsChange(tags: string[]) {
    this.hostWindowRequests.tagsChanged(tags);
  }

  get state() {
    return this.store.state;
  }

  async handleTagGroupChange(tagGroupId: string | undefined) {
    if (this.state.profile) {
      if (!tagGroupId) {
        this.state.profile.tagGroupId = undefined;
        this.state.tagGroup = undefined;

        return;
      }

      const { tagGroup } = this.state;
      if (tagGroup && tagGroup.id === tagGroupId) {
        return;
      }

      await this.spinnerService
        .getTagGroup(tagGroupId)
        .then((group) => {
          if (this.state.profile) {
            this.state.tagGroup = group;
            this.state.profile.tagGroupId = tagGroupId;
          }
        })
        .catch((e) => logger.error({ message: "Couldn't load tag group", error: e }));
    }
  }

  resetState(reinit: boolean) {
    if (!reinit) {
      this.authService.forgetCurrentGuest();
    }

    localStorage.removeItem(
      staticConfig.localStorageKeyPrefix + ":" + this.orgId + ":wasAutoCallingFirstMessageSent"
    );

    this.continueConversationToken = this.joinMeetingToken = this.cancelMeetingToken = undefined;

    const sharedPopupStore = new SharedPopupStoreClass();
    sharedPopupStore.state.orgConfig = this.state.shared.orgConfig;
    // FIXME: see CLSR-2948, StorageService should be removed ?
    this.storageService = new StorageService(this.orgId, sharedPopupStore, isSharedPopupState);
    this.store.reset(sharedPopupStore.state, this.spinnerService);
    this.artichokeService.disconnect();
  }

  toJSON() {
    return "{CONTEXT}";
  }
}
